import React, { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtAmount } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileSpreadsheet, FileText, Receipt, AlertCircle, Pencil } from "lucide-react";
import { toast } from "sonner";

export const downloadFile = async (url, params, filename) => {
  try {
    const resp = await api.get(url, { params, responseType: "blob" });
    const objUrl = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objUrl);
  } catch (e) {
    toast.error(formatErr(e));
  }
};

export const ExportButtons = ({ url, params, name, testid }) => (
  <div className="flex gap-2">
    <Button size="sm" variant="outline" data-testid={`${testid}-xlsx`} onClick={() => downloadFile(url, { ...params, fmt: "xlsx" }, `${name}.xlsx`)} className="gap-1 bg-white">
      <FileSpreadsheet className="w-3.5 h-3.5" />Excel
    </Button>
    <Button size="sm" variant="outline" data-testid={`${testid}-pdf`} onClick={() => downloadFile(url, { ...params, fmt: "pdf" }, `${name}.pdf`)} className="gap-1 bg-white">
      <FileText className="w-3.5 h-3.5" />PDF
    </Button>
  </div>
);

const FILTERS = [["pending", "Not yet invoiced"], ["invoiced", "Invoiced"], ["all", "All"]];

const Undecided = ({ items, onChanged }) => {
  const set = async (ids, billable) => {
    try { await api.post("/compliance/invoice-register/billing", { payment_ids: ids, billable }); toast.success("Billing updated"); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!items.length) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md p-4" data-testid="register-undecided">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertCircle className="w-4 h-4" />Billing not decided ({items.length})</div>
      <p className="text-xs text-amber-800 mt-1">These filings are done but not marked To invoice / Not to invoice. Set a fee later by clicking the cell on the board.</p>
      <ul className="mt-3 divide-y divide-amber-200">
        {items.map((u) => (
          <li key={u.payment_id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`register-undecided-${u.payment_id}`}>
            <span className="flex-1 min-w-[200px]"><b>{u.entity}</b> · {u.name} <span className="text-xs text-slate-500">filed {fmtDate(u.filed_on)}</span></span>
            <Button size="sm" variant="outline" data-testid={`register-undecided-bill-${u.payment_id}`} onClick={() => set([u.payment_id], true)} className="h-7 bg-white">To invoice</Button>
            <Button size="sm" variant="ghost" data-testid={`register-undecided-nobill-${u.payment_id}`} onClick={() => set([u.payment_id], false)} className="h-7">Not to invoice</Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InvoiceNoDialog = ({ cl, onClose, onSaved }) => {
  const [no, setNo] = useState("");
  const [dt, setDt] = useState("");
  useEffect(() => {
    if (cl) { setNo(cl.invoice_no || ""); setDt(cl.invoice_date || format(new Date(), "yyyy-MM-dd")); }
  }, [cl]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/compliance/invoice-register/mark", { payment_ids: cl.items.map((i) => i.payment_id), invoiced: true, invoice_no: no.trim(), invoice_date: dt });
      toast.success(`${cl.entity} marked invoiced (${no.trim()})`);
      onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={!!cl} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="invoice-no-dialog">
        <DialogHeader><DialogTitle>Invoice for {cl?.entity}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice no.</Label>
            <Input data-testid="invoice-no-input" autoFocus value={no} onChange={(e) => setNo(e.target.value)} maxLength={60} placeholder="e.g. KMS/26-27/0142" className="mt-1 mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice date</Label>
            <Input data-testid="invoice-date-input" type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="mt-1 mono" />
          </div>
          <div className="text-xs text-slate-500">{cl?.items.length} filing(s) · {fmtAmount(cl?.total || 0, "INR")}</div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="invoice-no-cancel">Cancel</Button>
            <Button type="submit" data-testid="invoice-no-save" disabled={!no.trim() || !dt} className="bg-blue-700 hover:bg-blue-800 text-white">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ClientBlock = ({ cl, idx, onChanged, onAskInvoice }) => {
  const mark = async (invoiced) => {
    if (invoiced) return onAskInvoice(cl);
    try {
      await api.post("/compliance/invoice-register/mark", { payment_ids: cl.items.map((i) => i.payment_id), invoiced });
      toast.success(invoiced ? `${cl.entity} marked invoiced` : `${cl.entity} moved back to not invoiced`);
      onChanged();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`register-client-${idx}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="font-semibold flex-1">{cl.entity}</div>
        {cl.invoice_no && (
          <span className="text-xs text-slate-600 flex items-center gap-1" data-testid={`register-client-invno-${idx}`}>
            Inv <b className="mono">{cl.invoice_no}</b> · {fmtDate(cl.invoice_date)}
            <button data-testid={`register-client-invno-edit-${idx}`} onClick={() => onAskInvoice(cl)} className="text-slate-400 hover:text-blue-700"><Pencil className="w-3 h-3" /></button>
          </span>
        )}
        <span className="mono text-sm font-semibold" data-testid={`register-client-total-${idx}`}>{fmtAmount(cl.total, "INR")}</span>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <Checkbox data-testid={`register-client-invoiced-${idx}`} checked={cl.invoiced} onCheckedChange={(v) => mark(v === true)} />Mark invoiced
        </label>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {cl.items.map((it) => (
            <tr key={it.payment_id} className="border-b border-slate-50 last:border-0">
              <td className="px-4 py-2">{it.name}</td>
              <td className="px-2 py-2 text-xs text-slate-500 mono">Due {fmtDate(it.due_date)}</td>
              <td className="px-2 py-2 text-xs text-emerald-700 mono">Filed {fmtDate(it.filed_on)}</td>
              <td className="px-4 py-2 text-right mono">{it.fee != null ? fmtAmount(it.fee, "INR") : <span className="text-amber-700 text-xs">No fee set</span>}</td>
              <td className="px-4 py-2 text-right text-[10px] uppercase tracking-wider">{it.invoiced ? <span className="text-emerald-700">Invoiced</span> : <span className="text-slate-400">Open</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function InvoiceRegister({ month, owner, onChanged }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [askInv, setAskInv] = useState(null);
  const params = { month, status, ...(owner !== "all" && { owner_id: owner }) };
  const load = useCallback(async () => {
    try { setData((await api.get("/compliance/invoice-register", { params: { month, status, ...(owner !== "all" && { owner_id: owner }) } })).data); } catch (e) { toast.error(formatErr(e)); }
  }, [month, status, owner]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };

  return (
    <div className="mt-5 space-y-4" data-testid="invoice-register">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex border border-slate-200 rounded-md bg-white overflow-hidden">
          {FILTERS.map(([k, label]) => (
            <button key={k} data-testid={`register-filter-${k}`} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 text-xs transition-colors ${status === k ? "bg-blue-700 text-white" : "hover:bg-slate-50"}`}>{label}</button>
          ))}
        </div>
        {data && (
          <div className="text-sm flex items-center gap-2" data-testid="register-grand-total">
            <Receipt className="w-4 h-4" />{data.count} filing(s) · Grand total <b className="mono">{fmtAmount(data.grand_total, "INR")}</b>
          </div>
        )}
        <div className="ml-auto"><ExportButtons url="/compliance/invoice-register/export" params={params} name={`invoice-register-${month}`} testid="register-export" /></div>
      </div>
      {data && <Undecided items={data.undecided} onChanged={changed} />}
      {data && data.clients.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500" data-testid="register-empty">Nothing to show for this filter.</div>}
      {data?.clients.map((cl, i) => <ClientBlock key={cl.entity} cl={cl} idx={i} onChanged={changed} onAskInvoice={setAskInv} />)}
      <InvoiceNoDialog cl={askInv} onClose={() => setAskInv(null)} onSaved={() => { setAskInv(null); changed(); }} />
    </div>
  );
}
